export interface ErrorMessageOptions {
	title: string;
	message: string;
	suggestions?: string[];
	error?: Error;
	context?: Record<string, unknown>;
}

export const errorMessages = {
	apiKeyMissing: (): ErrorMessageOptions => ({
		title: "APIキーが設定されていません",
		message: "ElevenLabs APIキーとVoice IDが必要です。",
		suggestions: [
			"左上のメニューから「音声設定」を開く",
			"ElevenLabs APIキーを入力し、「Voice選択」でVoiceを選ぶ",
			"再度お試しください",
		],
	}),

	audioGenerationFailed: (error: Error): ErrorMessageOptions => ({
		title: "音声生成に失敗しました",
		message: "音声の生成中にエラーが発生しました。",
		suggestions: [
			"APIキーが正しいか確認してください",
			"インターネット接続を確認してください",
			"ElevenLabsの利用上限を確認してください（https://elevenlabs.io/app/usage）",
			"テキストが長すぎる場合は分割してください",
		],
		error,
	}),

	audioUploadFailed: (error: Error): ErrorMessageOptions => ({
		title: "音声ファイルのアップロードに失敗しました",
		message: "音声ファイルの読み込み中にエラーが発生しました。",
		suggestions: [
			"サポートされている音声形式（MP3, WAV, M4Aなど）か確認してください",
			"ファイルが破損していないか確認してください",
			"別の音声ファイルで試してください",
		],
		error,
	}),

	videoGenerationFailed: (error: Error): ErrorMessageOptions => ({
		title: "動画生成に失敗しました",
		message: "動画の生成中にエラーが発生しました。",
		suggestions: [
			"すべてのシーンに音声が生成されているか確認してください",
			"ブラウザのメモリが不足している可能性があります。他のタブを閉じてください",
			"シーン数が多い場合は分割して生成してください",
			"ページを再読み込みして最初からやり直してください",
		],
		error,
	}),

	webCodecsUnsupported: (): ErrorMessageOptions => ({
		title: "このブラウザでは動画を生成できません",
		message: "動画生成に必要な WebCodecs API がこのブラウザにありません。",
		suggestions: [
			"Chrome、Edge、Opera の最新版、または Safari 16.4 以降をお使いください",
			"ブラウザを最新版に更新してください",
		],
	}),

	aacEncoderUnavailable: (): ErrorMessageOptions => ({
		title: "音声エンコーダーを読み込めませんでした",
		message: "動画の音声に必要なエンコーダーの読み込みに失敗しました。",
		suggestions: [
			"インターネット接続を確認してください",
			"しばらく待ってから再試行してください",
		],
	}),

	audioNormalizationFailed: (error: Error): ErrorMessageOptions => ({
		title: "音量正規化に失敗しました",
		message: "音量の正規化処理中にエラーが発生しました。",
		suggestions: [
			"設定で音量正規化を無効にして再試行してください",
			"音声ファイルが正しく生成されているか確認してください",
			"ブラウザのメモリが不足している可能性があります。他のタブを閉じてください",
			"ページを再読み込みして最初からやり直してください",
		],
		error,
	}),

	textCountMismatch: (
		textCount: number,
		imageCount: number
	): ErrorMessageOptions => {
		const diff = textCount - imageCount;
		return {
			title: "テキストと画像の数が一致しません",
			message: `テキスト: ${textCount}行、画像: ${imageCount}枚`,
			suggestions:
				diff > 0
					? [
							`テキストを${diff}行削除してください`,
							`または画像を${diff}枚追加してください`,
							"改行区切りで入力されているか確認してください",
					  ]
					: [
							`テキストを${-diff}行追加してください`,
							`または画像を${-diff}枚削除してください`,
							"改行区切りで入力されているか確認してください",
					  ],
		};
	},

	imageTooLarge: (
		fileName: string,
		fileSize: number,
		maxSize: number
	): ErrorMessageOptions => ({
		title: "画像ファイルが大きすぎます",
		message: `「${fileName}」のサイズが制限を超えています。\nファイルサイズ: ${(
			fileSize /
			1024 /
			1024
		).toFixed(2)}MB\n制限: ${(maxSize / 1024 / 1024).toFixed(0)}MB`,
		suggestions: [
			"画像を圧縮してください",
			"画像の解像度を下げてください",
			"別の画像形式（JPEG、WebPなど）を試してください",
		],
	}),

	noValidImages: (): ErrorMessageOptions => ({
		title: "有効な画像がありません",
		message: "選択またはドロップしたファイルに有効な画像が含まれていません。",
		suggestions: [
			"サポートされている画像形式（JPEG、PNG、WebP、GIF）を使用してください",
			"ファイルサイズが制限（15MB）以下か確認してください",
			"ファイルが破損していないか確認してください",
		],
	}),

	totalImageSizeTooLarge: (
		totalSize: number,
		maxSize: number
	): ErrorMessageOptions => ({
		title: "画像の合計サイズが大きすぎます",
		message: `選択された画像の合計サイズが制限を超えています。\n合計サイズ: ${(
			totalSize /
			1024 /
			1024
		).toFixed(2)}MB\n制限: ${(maxSize / 1024 / 1024).toFixed(0)}MB`,
		suggestions: [
			"いくつかの画像を削除してください",
			"画像を圧縮してからアップロードしてください",
			"画像を分割して複数回に分けてアップロードしてください",
		],
	}),

	audioFileTooLarge: (
		fileName: string,
		fileSize: number,
		maxSize: number
	): ErrorMessageOptions => ({
		title: "音声ファイルが大きすぎます",
		message: `「${fileName}」のサイズが制限を超えています。\nファイルサイズ: ${(
			fileSize /
			1024 /
			1024
		).toFixed(2)}MB\n制限: ${(maxSize / 1024 / 1024).toFixed(0)}MB`,
		suggestions: [
			"音声を圧縮してください（ビットレートを下げる）",
			"音声の長さを短くしてください",
			"別の音声形式（MP3など）を試してください",
		],
	}),

	unsupportedAudioFormat: (
		fileName: string,
		fileType: string
	): ErrorMessageOptions => ({
		title: "サポートされていない音声形式です",
		message: `「${fileName}」の形式（${fileType}）はサポートされていません。`,
		suggestions: [
			"サポートされている音声形式（MP3、WAV、OGG）を使用してください",
			"音声変換ツールで形式を変換してください",
			"別の音声ファイルを試してください",
		],
	}),

	rateLimitExceeded: (retryAfter?: number): ErrorMessageOptions => ({
		title: "ElevenLabs APIのレート制限に達しました",
		message: retryAfter
			? `約${retryAfter}秒後に自動的に再試行されます。`
			: "音声生成のリクエストが多すぎます。",
		suggestions: [
			"数分待ってから再試行してください",
			"ElevenLabsの利用状況を確認してください（https://elevenlabs.io/app/usage）",
			"無料プランの場合は月間10,000文字の制限があります",
			"同時に複数のシーンを生成している場合は1つずつ試してください",
			"有料プランへのアップグレードを検討してください",
		],
	}),

	quotaExceeded: (): ErrorMessageOptions => ({
		title: "ElevenLabs APIの月間利用上限に達しました",
		message: "今月の文字数制限を超過しています。",
		suggestions: [
			"来月まで待つか、有料プランへアップグレードしてください",
			"ElevenLabsの利用状況を確認してください（https://elevenlabs.io/app/usage）",
			"別のAPIキーを使用することも検討してください",
		],
	}),
};

export interface NoticeMessageOptions {
	title: string;
	description: string;
	actionLabel: string;
}

/**
 * エラーではなく利用者への案内。文言はerrorMessagesと同じく一箇所で管理する（ADR-0011）。
 */
export const noticeMessages = {
	updateAvailable: (afterLoadError: boolean): NoticeMessageOptions => ({
		title: "新しいバージョンがあります",
		description: afterLoadError
			? "読み込みに失敗しました。更新すると最新の状態になります。設定は引き継がれます。"
			: "更新すると最新の状態になります。設定は引き継がれます。",
		actionLabel: "更新",
	}),
};
